import { k, t } from "./i18n/i18n";

/** Échelons nommés en clair (fiche d'unité, ordre de bataille). Côté ami, selon l'arme : batteries et pièces
 *  de l'artillerie, escadrons et pelotons de la cavalerie, de l'arme blindée et du train. L'ennemi garde ses
 *  propres termes (« compagnie de chars »). */

const ECHELON: Record<string, string> = {
  EQUIPE: k("Équipe"),
  GROUPE: k("Groupe"),
  SECTION: k("Section"),
  COMPAGNIE: k("Compagnie"),
  BATAILLON: k("Bataillon"),
  REGIMENT: k("Régiment"),
  BRIGADE: k("Brigade"),
  DIVISION: k("Division"),
  CORPS: k("Corps d'armée"),
};

const ARM_ECHELON: Record<string, Record<string, string>> = {
  ARTILLERIE: { COMPAGNIE: k("Batterie"), GROUPE: k("Pièce") },
  BLINDE: { COMPAGNIE: k("Escadron"), SECTION: k("Peloton") },
  CAVALERIE: { COMPAGNIE: k("Escadron"), SECTION: k("Peloton") },
  TRAIN: { COMPAGNIE: k("Escadron"), SECTION: k("Peloton") },
};

export function echelonName(
  echelon: string | null | undefined,
  arm?: string | null,
  friendly = true,
): string | undefined {
  const key = echelon ?? "";
  const name = (friendly && arm ? ARM_ECHELON[arm]?.[key] : undefined) ?? ECHELON[key];
  return name ? t(name) : undefined;
}

/** Échelon moteur de chaque échelon APP-6 (chiffres 9 et 10 du SIDC). La section OTAN (13, « •• ») a son nom. */
const APP6_ECHELON: Record<string, string> = {
  "11": "EQUIPE",
  "12": "GROUPE",
  "13": "SECTION",
  "14": "SECTION",
  "15": "COMPAGNIE",
  "16": "BATAILLON",
  "17": "REGIMENT",
  "18": "BRIGADE",
  "21": "DIVISION",
  "22": "CORPS",
};

/** Échelon porté par le symbole d'une unité construite (SIDC), nommé comme `echelonName` : l'échelon APP-6 choisi
 *  dans l'éditeur, qui peut différer de l'échelon moteur (une compagnie placée directement sous une brigade). */
export function echelonNameFromSidc(sidc: string | null | undefined, arm?: string | null, friendly = true): string | undefined {
  const code = sidc && /^\d{20}$/.test(sidc) ? sidc.slice(8, 10) : "";
  if (code === "13") return t(k("Section OTAN"));
  return echelonName(APP6_ECHELON[code], arm, friendly);
}

/** Cadre force opérationnelle porté par un SIDC (chiffre 8 : 4 ou 6). */
export function taskForceFromSidc(sidc: string | null | undefined): boolean {
  return Boolean(sidc && /^\d{20}$/.test(sidc) && (sidc[7] === "4" || sidc[7] === "6"));
}

/** Groupement tactique d'une unité construite en force opérationnelle : « S/GTIA » à la compagnie, « GTIA » au
 *  bataillon et au régiment ; null sans force opérationnelle ou à un autre échelon (une brigade ou une section en
 *  force opérationnelle garde le cadre du symbole, sans nom de groupement tactique). */
export function taskOrgFromSidc(sidc: string | null | undefined): "S/GTIA" | "GTIA" | null {
  if (!taskForceFromSidc(sidc)) return null;
  const echelon = (sidc as string).slice(8, 10);
  return echelon === "15" ? "S/GTIA" : echelon === "16" || echelon === "17" ? "GTIA" : null;
}
